import { useState, useEffect, useCallback, useRef } from "react";
import { fetchProductResults, fetchAutocompleteResults, fetchSuggestResults, fetchTrendingResults, fetchRecommendationResults } from '../../js/searchspring';
import { trackProductImpression, trackProductClick, trackSearch, trackAutocomplete, trackRecommendationImpression } from '../../js/searchspringBeacon';
import shopify from "frontend/js/shopify";
import helpers from '../../js/helpers';
import { debounce } from "lodash";
import { DEFAULT_TRENDING_QUERIES } from '../../js/contants';

function useSearchSpring(collection = null) {
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsFacets, setSearchResultsFacets] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestResults, setSuggestResults] = useState({});
  const [trendingResults, setTrendingResults] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [crossSellProducts, setCrossSellProducts] = useState([]);
  const [dynamicRecommendations, setDynamicRecommendations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [sortingOptions, setSortingOptions] = useState([]);
  const [sortOption, setSortOption] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const prevPageRef = useRef(currentPage);

  const itemsPerPage = 16;

  const handleProductListing = useCallback(
    debounce(async (page = 1, filters = "") => {
      try {
        setLoading(true);
        const query = {
          resultsPerPage: itemsPerPage,
          page,
        };

        if (collection) {
          query['bgfilter.collection_handle'] = collection.handle;
        }

        if (sortOption) {
          const { field, direction } = sortOption;
          query[`sort.${field}`] = direction;
        }

        const productData = await fetchProductResults(query, filters);

        setTotalResults(productData.pagination.totalResults);
        setSortingOptions(productData.sorting?.options || []);

        const transformedProducts = await transformProducts(productData.results);

        if (page === 1) {
          setProducts(transformedProducts);
        } else {
          setProducts((prevProducts) => [...prevProducts, ...transformedProducts]);
        }

        setFilters(productData.facets);

        // Track product impressions for collection/search
        if (transformedProducts.length > 0) {
          const context = collection ? 'collection' : 'search';
          transformedProducts.forEach((product, index) => {
            trackProductImpression(product, context, {
              position: ((page - 1) * itemsPerPage) + index + 1,
              collectionHandle: collection?.handle,
              totalResults: productData.pagination.totalResults
            });
          });
        }

      } catch (err) {
        console.error('Failed to fetch search results:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }, 300),
    [collection, sortOption]
  );

  const handleTrendingResults = useCallback(async () => {
    try {
      const trendingData = await fetchTrendingResults();
      let trendingQueries = trendingData?.trending?.queries || [];
      if (trendingQueries.length === 0) {
        trendingQueries = DEFAULT_TRENDING_QUERIES;
      }
      setTrendingResults(trendingQueries);
    } catch (error) {
      console.error('Failed to fetch trending results:', error);
      setTrendingResults([]);
    }
  }, []);

  // Enhanced autocomplete with tracking
  const handleAutocomplete = useCallback(async (q) => {
    try {
      setLoading(true);
      const query = { q };
      const searchData = await fetchAutocompleteResults(query);
      const transformedProducts = await transformProducts(searchData.results);

      setSearchResults(transformedProducts);
      setSearchResultsFacets(searchData.facets);

      // Track search
      trackSearch(q, transformedProducts.length, {
        type: 'autocomplete',
        resultCount: transformedProducts.length
      });

      // Track product impressions for search results
      transformedProducts.forEach((product, index) => {
        trackProductImpression(product, 'search_autocomplete', {
          position: index + 1,
          query: q
        });
      });

      return transformedProducts;
    } catch (err) {
      console.error('Failed to fetch autocomplete results:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Enhanced suggestion results handling
  const handleSuggestResults = useCallback(async (q) => {
    try {
      const query = { q };
      const suggestData = await fetchSuggestResults(query);
      setSuggestResults(suggestData);
    } catch (err) {
      console.error('Failed to fetch suggest results:', err);
      setError(err);
    }
  }, []);

  // Add a ref to track what's already been tracked
  const trackedRecommendations = useRef(new Set());

  // Enhanced recommendation listing with tracking
  const handleRecommendationListing = useCallback(async (productId, type) => {
    try {
      console.log('Fetching recommendations:', { productId, type });
      const productData = await fetchRecommendationResults(productId, type);
      const transformedProducts = await transformProducts(productData[0].results);

      // console.log('Transformed recommendation products:', transformedProducts);

      if (type === 'similar') {
        setSimilarProducts(transformedProducts);
      } else if (type === 'cross-sell') {
        setCrossSellProducts(transformedProducts);
      } else {
        setDynamicRecommendations(prev => {
          const updated = {
            ...prev,
            [type]: transformedProducts
          };
          return updated;
        });
      }

      // Only track if we have products and haven't tracked this combination before
      if (transformedProducts.length > 0) {
        const trackingKey = `${type}-${productId}-${transformedProducts.length}`;
        
        if (!trackedRecommendations.current.has(trackingKey)) {
          trackedRecommendations.current.add(trackingKey);
          console.log('Tracking recommendation impressions for profile:', type);
          
          trackRecommendationImpression(transformedProducts, type, {
            contextProductId: productId,
            resultCount: transformedProducts.length
          });
        } else {
          console.log('Recommendations already tracked for:', trackingKey);
        }
      }

    } catch (error) {
      console.error(`Error fetching ${type} products:`, error);
    }
  }, []);

  // Clear tracking on unmount
  useEffect(() => {
    return () => {
      trackedRecommendations.current.clear();
    };
  }, []);

  const clearSearchResults = () => {
    setSearchResults([]);
    setSearchResultsFacets([]);
    setSuggestResults({});
  };

  function calculateBundleTotals(data) {
    const bundleReferences = data.find(item => item.key === "bundles_product_references")?.references?.edges || [];

    const bundlePrices = bundleReferences.map(bundle => {
        const products = bundle.node.bundle_products?.references?.edges || [];
        
        // Extract min and max prices for each product
        const minPrices = products.map(product => parseFloat(product.node.priceRange.minVariantPrice.amount));
        const maxPrices = products.map(product => parseFloat(product.node.priceRange.maxVariantPrice.amount));
    
        return {
            min: Math.min(...minPrices),
            max: Math.max(...maxPrices)
        };
    });
    
  
    const totalMin = bundlePrices.reduce((sum, bundle) => sum + bundle.min, 0).toFixed(2);
    const totalMax = bundlePrices.reduce((sum, bundle) => sum + bundle.max, 0).toFixed(2);

    const discount = parseFloat(data.find(item => item.key === "bundles_discount_percentage")?.value[0]) || 0;

  
     const discountedMin = (totalMin - (totalMin * discount / 100)).toFixed(2);
     const discountedMax = (totalMax - (totalMax * discount / 100)).toFixed(2);
    
    return { totalMin, totalMax, discountedMin,  discountedMax };
}

  const transformProducts = useCallback(async (products) => {
    if (!products || products.length === 0) return [];

    const productIds = [...new Set(products.map(product => product.uid || product.mappings?.core?.uid))];

    try {
      const shopifyProducts = await shopify.getShopifyProductList(productIds);

      const productsWithColorPattern = await Promise.all(
        shopifyProducts.map(async (shopifyProduct) => {
          const { metafields, productType } = shopifyProduct;
          const productSiblingsArr = metafields.find(item => item.key === "product_siblings");
          let bundleTotals = {};
          if(productType == 'Bundle') {
            bundleTotals = calculateBundleTotals(metafields);
        
          }
      

          let colorPatterns = [];

          if (productSiblingsArr) {
            const siblingsRefArr = productSiblingsArr.references.edges;
            const productSiblingIds = siblingsRefArr.map(sibling => sibling.node.metafields[0]?.value);
            
            if (productSiblingIds) {
              // Fetch color pattern data for each sibling
              colorPatterns = await Promise.all(productSiblingIds.map(async (id) => {
                if (id){
                  const colorPatternQuery = shopify.getColorPatternQuery(helpers.cleanupGlobalId(id));
                  if (colorPatternQuery) {
                    const colorPatternData = await shopify.fetchGraphQL(colorPatternQuery);
                    return colorPatternData?.metaobject?.fields || [];
                  }
                }
                return  [];
              }));

              colorPatterns = colorPatterns.map((colorPatternArray, index) => {
                const colorPatternObject = {};
                colorPatternArray.forEach(item => {
                  if (item.key === "image" && item.reference?.previewImage?.src) {
                    colorPatternObject[item.key] = item.reference.previewImage.src;
                  } else {
                    colorPatternObject[item.key] = item.value;
                  }
                });
                colorPatternObject['url'] = '/products/' + siblingsRefArr[index]?.node?.handle || '';

                return colorPatternObject;
              });
            }
          }

          return {
            ...shopifyProduct,
            colorPatterns,
            ...(productType === 'Bundle' && bundleTotals && { bundlePrices: bundleTotals })
          };
        })
      );

      const productsWithTog = shopifyProducts.map(product => {
        const { metafields } = product;
        const productTogArr = metafields.find(item => item.key === "tog_rating");

        let productTogs = [];

        if (productTogArr) {
          const togRefArr = productTogArr.references.edges;

          productTogs = togRefArr.map(togNode => {
            const togObj = {};
            const node = togNode.node;

            // Extract the tog_title and tog_colour
            togObj['tog_colour'] = node.tog_colour?.value;
            togObj['tog_title'] = node.tog_title?.value;

            return togObj;
          });
        }

        return {
          ...product,
          productTogs,
        };
      });

      return products.map(product => ({
        ...product,
        ...productsWithColorPattern.find(shopifyProduct => shopifyProduct.id === parseInt(product.uid || product.mappings?.core?.uid)),
        ...productsWithTog.find(shopifyProduct => shopifyProduct.id === parseInt(product.uid || product.mappings?.core?.uid)),
      }));
    } catch (err) {
      console.error('Failed to transform products using Shopify:', err);
      return products;
    }
  }, []);

  const loadMoreProducts = () => {
    if (currentPage !== prevPageRef.current) return; // Prevent duplicate increments
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const changeSort = useCallback(
    debounce((sort) => {
      setSortOption(sort);
      setCurrentPage(1);
      setLoading(true);
    }, 300), []
  );

  const changeFilter = (filter) => {
    const queryStringUrl = helpers.updateUrlAndFilters(filter);
    setCurrentPage(1);
    setSelectedFilters(queryStringUrl);
  };

  // Enhanced search with tracking
  const autocompleteSearch = (q) => {
    setSearchQuery(q);
    handleSuggestResults(q);
    handleAutocomplete(q);
  };

  const fetchSimilarProducts = (productId) => {
    handleRecommendationListing(productId, 'similar')
  }

  const fetchCrossSellProducts = (productId) => {
    handleRecommendationListing(productId, 'cross-sell');
  };

  const fetchCustomRecommendations = useCallback((productId, customTag) => {
    if (!customTag) {
      console.warn('customTag is required for custom recommendations');
      return;
    }
    
    console.log(`Fetching recommendations for profile: ${customTag}`, productId ? `with product context: ${productId}` : 'without product context');
    handleRecommendationListing(productId, customTag);
  }, [handleRecommendationListing]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialFilters = [];

    params.forEach((value, key) => {
      initialFilters.push(`${key}=${value}`);
    });

    const filterString = initialFilters.join("&");
    const selectedFilterString = filterString ? `?${filterString}` : "";
    const isSearchPage = params.has("q");

    if (isSearchPage || collection) {
      handleProductListing(currentPage, selectedFilterString);
      prevPageRef.current = currentPage;
    }
  }, [collection, currentPage, sortOption]);

  useEffect(() => {
    if (selectedFilters) {
      handleProductListing(currentPage, selectedFilters);
    }
  }, [selectedFilters]);

  // useEffect(() => {
  //   if (searchQuery) {
  //     handleAutocomplete(searchQuery);
  //   }
  // }, [searchQuery, handleAutocomplete]);

  useEffect(() => {
    if (isSearchOpen) {
      handleTrendingResults();
    }
  }, [isSearchOpen, handleTrendingResults]);

  const toggleSearchBar = (isOpen) => {
    setIsSearchOpen(isOpen);
  };

  return {
    products,
    filters,
    loading,
    error,
    totalResults,
    loadMoreProducts,
    sortingOptions,
    currentPage,
    changeSort,
    changeFilter,
    autocompleteSearch,
    toggleSearchBar,
    searchResults,
    searchResultsFacets,
    suggestResults,
    trendingResults,
    clearSearchResults,
    fetchSimilarProducts,
    similarProducts,
    fetchCrossSellProducts,
    crossSellProducts,
    fetchCustomRecommendations,
    dynamicRecommendations,
  };
}

export default useSearchSpring;
